import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { TimeService } from '@services/time-service';
import { AppConfig } from 'client/app/app.config';
import { FollowUpService } from '@services/followup-service';

export interface ResolveProgressFollowUpResult {
  id: string;
  followUp: any;
}

export interface SelfCheckupWindowVariant {
  start_day: string;
  end_day: string;
  class: string[];
}

export interface SelfCheckupWindowContext {
  variant: SelfCheckupWindowVariant;
  deliveredOn: Date | string;
  followUp?: { followUpCount?: number } | null;
}

export interface SelfCheckupWindowResult {
  isWithinSelfCheckupWindow: boolean;
  shouldShowChangedSelfCheckupUi: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SelfCheckupService {
  constructor(
    private readonly connectionService: ConnectionService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly timeService: TimeService,
    private readonly appConfig: AppConfig,
    private readonly followUpService: FollowUpService,
  ) {}

  getFollowUpId(followUp: any): string | null {
    if (!followUp) return null;
    return followUp.id ?? followUp.objectId ?? null;
  }

  computeSelfCheckupWindow(params: SelfCheckupWindowContext): SelfCheckupWindowResult {
    const { variant, deliveredOn, followUp } = params;
    const deliveredDate = new Date(deliveredOn);
    const daysSinceDelivered = this.timeService.differenceInDays(new Date(), deliveredDate);

    const expStartDay = Number(variant.start_day);
    const expEndDay = Number(variant.end_day);

    const isWithinSelfCheckupWindow = daysSinceDelivered >= expStartDay && daysSinceDelivered <= expEndDay;
    const shouldShowChangedSelfCheckupUi = (followUp?.followUpCount ?? 0) > 1;

    return { isWithinSelfCheckupWindow, shouldShowChangedSelfCheckupUi };
  }

  async resolveProgressFollowUp(options: { markReady?: boolean } = {}): Promise<ResolveProgressFollowUpResult | null> {
    const followUp = await this.fetchStoredOrRecentProgressFollowUp();
    if (!followUp) return null;

    if (options.markReady) {
      await this.connectionService.markFollowUpReadyStatus(followUp.id);
    }

    return { id: followUp.id, followUp };
  }

  private fetchStoredOrRecentProgressFollowUp(): any {
    const storedId = this.localStorageService.getValue('currentProgressFollowUpId');
    const pendingState = this.appConfig.Shared.Followup.State.PENDING;

    if (storedId) {
      const fromStored = this.followUpService.findFollowUpById(storedId, pendingState);
      if (fromStored) return fromStored;
    }

    const recent = this.followUpService.findMostRecentPendingFollowUp(pendingState);
    if (recent) {
      this.localStorageService.setValue('currentProgressFollowUpId', recent.id);
    }
    return recent;
  }

  async navigateToFollowUpChatFromBanner(followUp: any): Promise<void> {
    const followUpId = this.getFollowUpId(followUp)
      ?? this.localStorageService.getValue('currentProgressFollowUpId');

    if (followUpId) {
      const state = followUp?.get?.('State') ?? followUp?.State;
      await this.createFollowUpIfNeeded(state);
      await this.navigateToFollowUpChat(followUpId);
      return;
    }

    const result = await this.resolveProgressFollowUp();
    if (result) {
      const state = result.followUp?.get?.('State') ?? result.followUp?.State;
      await this.createFollowUpIfNeeded(state);
      await this.navigateToFollowUpChat(result.id);
      return;
    }

    await this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  private async createFollowUpIfNeeded(followUpState: string): Promise<void> {
    if (followUpState !== this.appConfig.Shared.Followup.State.WAITING_FOR_IMAGE) {
      await this.connectionService.createFollowUp();
    }
  }

  async navigateToFollowUpChat(followUpId: string, options: { markReady?: boolean } = {}): Promise<boolean> {
    const { markReady = false } = options;

    if (markReady) {
      await this.connectionService.markFollowUpReadyStatus(followUpId);
    }

    this.localStorageService.delete('currentProgressFollowUpId');
    return this.router.navigate([`/chatV2/${followUpId}`], {
      queryParams: { type: 'followUp', fromSelfCaptureBanner: true },
    });
  }
}
